import { GROUP_AUTO_COLUMN_ID, ModuleRegistry } from '@ag-grid-community/core';
// AG Grid Modules
import { LicenseManager } from '@ag-grid-enterprise/core';
import { CsvExportModule } from '@ag-grid-community/csv-export';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model';
import { ClipboardModule } from '@ag-grid-enterprise/clipboard';
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export';
import { MenuModule } from '@ag-grid-enterprise/menu';
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter';
import { SideBarModule } from '@ag-grid-enterprise/side-bar';
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel';
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel';
import { isArray, isFunction, isNeObject, isNeString } from '@aretheyhappy/utils';
import I18N from '../I18N';
/**
 * Support column type filters built into AG Grid
 */
var AG_COLUMN_FILTER_TYPES;
(function (AG_COLUMN_FILTER_TYPES) {
    AG_COLUMN_FILTER_TYPES["NUMBER"] = "agNumberColumnFilter";
    AG_COLUMN_FILTER_TYPES["TEXT"] = "agTextColumnFilter";
    AG_COLUMN_FILTER_TYPES["DATE"] = "agDateColumnFilter";
    AG_COLUMN_FILTER_TYPES["MULTI"] = "agMultiColumnFilter";
    AG_COLUMN_FILTER_TYPES["SET"] = "agSetColumnFilter";
})(AG_COLUMN_FILTER_TYPES || (AG_COLUMN_FILTER_TYPES = {}));
/**
 * Copy a definition, recursing into plain objects and arrays. Functions and class
 * instances (formatters, renderers, etc.) are kept by reference since they can't be cloned.
 * @param value
 * @returns
 */
function cloneDef(value) {
    if (isArray(value))
        return value.map(cloneDef);
    if (value !== null && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
        return Object.fromEntries(Object.entries(value).map(([key, val]) => [key, cloneDef(val)]));
    }
    return value;
}
/**
 * Base class for columns and group columns
 */
class _ColHelper {
    constructor(colDef) {
        this.colDef = colDef;
    }
    /**
     * Create a copy of this helper so it can be modified without changing the original.
     * Group children are copied as well.
     * @returns
     */
    clone() {
        const copy = Object.create(Object.getPrototypeOf(this));
        copy.colDef = cloneDef(this.colDef);
        return copy;
    }
    /**
     * Set any value on the column not already exposed by a function
     * @param key
     * @param value
     * @returns
     */
    setAny(key, value) {
        this.colDef[key] = value;
        return this;
    }
    /**
     * Set the name of this column.
     * @param name
     */
    setHeaderName(name, setTooltip = true) {
        if (!isNeString(name))
            return this;
        this.colDef.headerName = name;
        if (setTooltip)
            this.setTooltip(name);
        return this;
    }
    setEmptyHeaderName() {
        this.colDef.headerName = '';
        return this;
    }
    /**
     * Set the tooltip for this column header
     * @param tooltip
     */
    setTooltip(tooltip) {
        if (!isNeString(tooltip))
            return this;
        this.colDef.headerTooltip = tooltip;
        return this;
    }
    /**
     * Set how this header should be shown if it is the child of another header.
     */
    setGroupVisibilityState(visibility) {
        this.colDef.columnGroupShow = visibility;
        return this;
    }
    /**
     * Set the type of filtering for this column
     *
     * @param type
     * @param params
     * @returns
     */
    setFilterType(type, params) {
        this.setFilterParams(params);
        this.colDef.filter = type;
        return this;
    }
    /**
     * Set filter parameter options for this column
     * @param params
     * @returns
     */
    setFilterParams(params) {
        this.colDef.filterParams = params;
        return this;
    }
}
/**
 * Helper to build out a column definition for a AgGrid table
 */
class ColHelper extends _ColHelper {
    constructor() {
        super({});
    }
    /**
     * Sets the field for the column. This is the value on the row payload
     * to put in the row cell for this column. Will also be used as the
     * header if a separate title is not provided.
     * @param field
     * @returns
     */
    setField(field) {
        if (!isNeString(field))
            return this;
        this.colDef.field = field;
        return this;
    }
    /**
     * Mark this column as pinned
     * @param pinned
     * @returns
     */
    setPinned(pinned) {
        this.colDef.pinned = pinned;
        return this;
    }
    /**
     * Set if this column can be sorted
     * @param sortable
     * @returns
     */
    setSortable(sortable) {
        this.colDef.sortable = sortable;
        return this;
    }
    /**
     * Set the data type for this column for sorting behavior
     * @param type
     * @returns
     */
    setDataType(type) {
        this.colDef.cellDataType = type;
        return this;
    }
    /**
     * Set column types. Used for existing column templates.
     * @param type
     */
    setType(type) {
        this.colDef.type = type;
        return this;
    }
    /**
     * Set a custom value formatter function for this column
     * @param func
     * @returns
     */
    setValueFormatter(func) {
        this.colDef.valueFormatter = func;
        return this;
    }
    /**
     * Set if this column is shown or hidden by default. Shown is the default value.
     * Hidden columns can be accessed in the sidebar.
     * @param value
     * @returns
     */
    setHide(value) {
        this.colDef.hide = value;
        return this;
    }
}
/**
 * Helper to build out a column group definition for a AgGrid table
 */
class ColGroupHelper extends _ColHelper {
    constructor() {
        super({
            children: []
        });
    }
    /**
     * Add new child column to this column
     * @param colDef
     */
    addChild(colDef) {
        if (!isNeObject(colDef))
            return this;
        if (colDef instanceof ColHelper)
            this.colDef.children.push(colDef.colDef);
        else
            this.colDef.children.push(colDef);
        return this;
    }
}
/**
 * Grid auto sizing types
 */
var AUTO_SIZE_TYPE;
(function (AUTO_SIZE_TYPE) {
    // Content will stretch to fit the table
    AUTO_SIZE_TYPE["FIT_GRID_WIDTH"] = "fitGridWidth";
    AUTO_SIZE_TYPE["FIT_PROVIDED_WIDTH"] = "fitProvidedWidth";
    // Columns will shrink to fit cell content
    AUTO_SIZE_TYPE["FIT_CELL_CONTENTS"] = "fitCellContents";
})(AUTO_SIZE_TYPE || (AUTO_SIZE_TYPE = {}));
/**
 * Helper to setup grid sizing and scaling
 */
class SizeStrategyHelper {
    constructor() {
        this.strategy = {
            type: AUTO_SIZE_TYPE.FIT_CELL_CONTENTS
        };
    }
    /**
     * Set auto size strategy type
     * @param type
     */
    setStrategyType(type) {
        this.strategy.type = type;
        return this;
    }
    /**
     * Set any value on the size strategy not already exposed by a function
     * @param key
     * @param value
     * @returns
     */
    setAny(key, value) {
        this.strategy[key] = value;
        return this;
    }
    /**
     * Create a copy of this helper so it can be modified without changing the original
     * @returns
     */
    clone() {
        const copy = new SizeStrategyHelper();
        copy.strategy = cloneDef(this.strategy);
        return copy;
    }
}
var MEAD_TABLE_COLUMN_TYPES;
(function (MEAD_TABLE_COLUMN_TYPES) {
    MEAD_TABLE_COLUMN_TYPES["US_DOLLAR"] = "us_dollar";
    MEAD_TABLE_COLUMN_TYPES["PERCENTAGE"] = "percentage";
    MEAD_TABLE_COLUMN_TYPES["NA_VALUE_DEFAULT"] = "na_value_default";
    MEAD_TABLE_COLUMN_TYPES["RIGHT_ALIGN"] = "right_align";
    MEAD_TABLE_COLUMN_TYPES["METRIC"] = "metric";
})(MEAD_TABLE_COLUMN_TYPES || (MEAD_TABLE_COLUMN_TYPES = {}));
const NA_VALUE_GETTER = {
    valueGetter: params => {
        if (params.colDef.field === undefined)
            return I18N.go('mead.n_a');
        if (params.data === undefined)
            return I18N.go('mead.n_a');
        const value = params.data[params.colDef.field];
        if (value === undefined || value === null)
            return I18N.go('mead.n_a');
        return value;
    }
};
const RIGHT_ALIGN = {
    headerClass: 'ag-right-aligned-header',
    cellClass: 'ag-right-aligned-cell'
};
const DEFAULT_COLUMN_TYPES = {
    [MEAD_TABLE_COLUMN_TYPES.US_DOLLAR]: {
        //baseDataType: 'number',
        valueFormatter: params => {
            if (params.value == null) {
                return '';
            }
            if (params.value < 0)
                return `-$${(-1 * params.value).toLocaleString()}`;
            return `$${params.value.toLocaleString()}`;
        }
    },
    [MEAD_TABLE_COLUMN_TYPES.PERCENTAGE]: {
        valueFormatter: params => {
            if (params.value == null) {
                return '';
            }
            return `${params.value.toLocaleString()}%`;
        }
    },
    [MEAD_TABLE_COLUMN_TYPES.NA_VALUE_DEFAULT]: NA_VALUE_GETTER,
    [MEAD_TABLE_COLUMN_TYPES.RIGHT_ALIGN]: RIGHT_ALIGN,
    [MEAD_TABLE_COLUMN_TYPES.METRIC]: Object.assign({}, NA_VALUE_GETTER, RIGHT_ALIGN),
};
/**
 * Config engine to help build out the values for an AgGridTable
 */
class TableEngine {
    constructor() {
        /** Columns (headers) **/
        // Columns for our table
        this.columnDefs = [];
        // Column types for our table
        this.columnTypes = DEFAULT_COLUMN_TYPES;
        // Default column values
        this.defaultColDef = {
            flex: 1,
            minWidth: 100, // Don't allow columns to be less than 100px
        };
        /** Table Config **/
        // Sizing strategy
        this.autoSizeStrategy = new SizeStrategyHelper().strategy;
        // Turn on or off pagination
        this.pagination = false;
        this.paginationPageSize = 100;
        this.paginationPageSizeSelector = [10, 25, 50, 100];
        // Enable cell range selection on the table
        this.enableRangeSelection = true;
        this.rowSelection = undefined;
        this.rowMultiSelectWithClick = false;
        this.selectAllEnabled = false;
        this.checkboxSelection = false;
        // Context menu for the table
        this.getContextMenuItems = () => {
            const result = [
                'copy',
                'copyWithHeaders',
                'copyWithGroupHeaders',
                'paste',
                'resetColumns',
                'csvExport',
                'excelExport'
            ];
            return result;
        };
        /** Events */
        this.rowClicked = () => { };
        /**
         * Tree structure support
         * https://www.ag-grid.com/archive/31.3.4/vue-data-grid/tree-data/
         * **/
        // Turn on or off tree data support (child nesting)
        this.treeData = false;
        // If groups should be expanded by default
        this.groupDefaultExpanded = false;
        // Column definition for the grouped column
        this.autoGroupColumnDef = {};
        // Function to return data mapping for tree structure
        this.getDataPath = (data) => { return [data]; };
        /** Row data */
        // Type of data rendered on the rows
        this.rowModelType = 'clientSide';
        // Custom row Id function
        this.getRowId = undefined;
        /** Lazy Loading */
        this.isServerSideGroup = undefined;
        this.getServerSideGroupKey = undefined;
        this.serverSideDatasource = undefined;
        /** Sidebar */
        // Sidebar options
        this.sideBar = undefined;
    }
    /**
     * Set the size strategy for this table
     * @param strategy
     * @returns
     */
    setSizeStrategy(strategy) {
        this.autoSizeStrategy = strategy instanceof SizeStrategyHelper ? strategy.strategy : strategy;
        return this;
    }
    /**
     * Set default column definiton
     * @param col
     * @returns
     */
    setDefaultColumnDef(col) {
        if (!isNeObject(col))
            return this;
        this.defaultColDef = col instanceof _ColHelper ? col.colDef : col;
        return this;
    }
    /**
     * Enabled multi row selection with checkboxes.
     * This will add a checkbox to the first column of the table.
     *
     * @param selectAllEnabled Whether to enable the "select all" checkbox in the header.
     * @returns
     */
    enableMultiRowSelection(selectAllEnabled = true) {
        this.rowSelection = 'multiple';
        this.selectAllEnabled = selectAllEnabled;
        if (this.columnDefs.length > 0) {
            this.columnDefs[0]['checkboxSelection'] = true;
            if (this.selectAllEnabled) {
                this.columnDefs[0]['headerCheckboxSelection'] = true;
            }
        }
        this.rowMultiSelectWithClick = true;
        this.columnDefs = [...this.columnDefs];
        return this;
    }
    /**
     * Disables multi row selection with checkboxes.
     * This will remove the checkbox from the first column of the table.
     *
     * @returns
     */
    disableMultiRowSelection() {
        this.rowSelection = undefined;
        if (this.columnDefs.length > 0) {
            this.columnDefs[0]['checkboxSelection'] = false;
            this.columnDefs[0]['headerCheckboxSelection'] = false;
        }
        this.rowMultiSelectWithClick = false;
        this.columnDefs = [...this.columnDefs];
        return this;
    }
    /**
     * Toggles multi row selection with checkboxes.
     * This will add/remove a checkbox to the first column of the table.
     *
     * @param selectAllEnabled Whether to enable the "select all" checkbox in the header.
     * @returns
     */
    toggleMultiRowSelection(selectAllEnabled = true) {
        this.selectAllEnabled = selectAllEnabled;
        if (this.rowSelection === 'multiple') {
            this.rowSelection = undefined;
            if (this.columnDefs.length > 0) {
                this.columnDefs[0]['checkboxSelection'] = false;
                this.columnDefs[0]['headerCheckboxSelection'] = false;
            }
            this.rowMultiSelectWithClick = false;
        }
        else {
            this.rowSelection = 'multiple';
            if (this.columnDefs.length > 0) {
                this.columnDefs[0]['checkboxSelection'] = true;
                if (this.selectAllEnabled) {
                    this.columnDefs[0]['headerCheckboxSelection'] = true;
                }
            }
            this.rowMultiSelectWithClick = true;
        }
        this.columnDefs = [...this.columnDefs];
        return this;
    }
    /**
     * Add an array of columns
     * @param cols
     * @returns
     */
    addCols(cols) {
        if (!isArray(cols))
            return this;
        this.columnDefs.push(...cols);
        return this;
    }
    /**
     * Add a single column
     * @param def
     * @returns
     */
    addCol(def) {
        this.columnDefs.push(def.colDef);
        return this;
    }
    /**
     * Add a new supported column type
     * @param name
     * @param def
     * @returns
     */
    addColType(name, def) {
        this.columnTypes[name] = def.colDef;
        return this;
    }
    /**
     * Set if pagination is enabled or not
     * @param value
     * @returns
     */
    setPagination(value) {
        this.pagination = value;
        return this;
    }
    /**
     * Set the page size for pagination. This is the number of rows per page.
     * @param value
     * @returns
     */
    setPaginationPageSize(value) {
        if (!isNeObject(value))
            return this;
        this.paginationPageSize = value;
        return this;
    }
    /**
     * Set the page size selector for pagination. This is the list of options for rows per page.
     * @param value
     * @returns
     */
    setPaginationPageSizeSelector(value) {
        if (!isArray(value))
            return this;
        this.paginationPageSizeSelector = value;
        return this;
    }
    /**
     * Toggle on or off range selection
     * @param value
     * @returns
     */
    setEnableRangeSelection(value) {
        this.enableRangeSelection = value;
        return this;
    }
    /**
     * Set row clicked function
     * @param value
     * @returns
     */
    setRowClickedFunc(value) {
        if (!isFunction(value))
            return this;
        this.rowClicked = value;
        return this;
    }
    /**
     * Set if tree data is enabled or not
     * @param value
     * @returns
     */
    setTreeDataEnabled(value) {
        this.treeData = value;
        return this;
    }
    /**
     * Set the function to retrieve row Ids.
     * params.data will have the row information
     * (params: GetRowIdParams<TData>): string;
     * @param value
     */
    setGetRowIdFunction(value) {
        this.getRowId = value;
        return this;
    }
    /**
     * Set if groups expand by default or not
     * @param value
     * @returns
     */
    setGroupDefaultExpanded(value) {
        this.groupDefaultExpanded = value;
        return this;
    }
    /**
     * Set function to fetch context menu items for the table.
     * @param value
     * @returns
     */
    setContextMenuItemsFunc(value) {
        if (!isFunction(value))
            return this;
        this.getContextMenuItems = value;
        return this;
    }
    /**
     * Set the config for grouping. The way this works is you first specify information
     * around the grouping column and how it should work. Then you specify a function
     * that tells the table how to get the hierarchy information from the row.
     *
     * @param autoGroupColumnDef - ColDef for the grouping column Set HeaderName by default
     * @param getDataPath
     * @returns
     */
    setTreeGrouping(autoGroupColumnDef, getDataPath) {
        if (!isNeObject(autoGroupColumnDef)) {
            console.error('autoGroupColumnDef must be a non empty object with column information.');
            return this;
        }
        if (!isFunction(getDataPath)) {
            console.error('getDataPath must be a function');
            return this;
        }
        this.treeData = true;
        this.autoGroupColumnDef = autoGroupColumnDef instanceof _ColHelper ? autoGroupColumnDef.colDef : autoGroupColumnDef;
        this.getDataPath = getDataPath;
        return this;
    }
    /**
     * Set the table into server side data mode.
     *
     * @param isServerSideGroup - This should be a function that returns a boolean if the sent row data
     * is a group. The function will be sent an entire rowData object. So for example if your row data
     * looks like this {uid: '1234', group: true, foo: 'bar'}. You could send a function like
     * (rowData) => { return rowData.group; }
     * This only needs to be provided for group fetching.
     * @param getServerSideGroupKey - This should be a function that returns a unique uid/key for this
     * row. The function will be sent an entire rowData object. So for example if your row data
     * looks like this {uid: '1234', group: true, foo: 'bar'}. You could send a function like
     * (rowData) => { return rowData.uid; }
     * It appears that it also works if you just return the entire `rowData` object as well. But you
     * will need to replace the `getRowId` function.
     * @param serverSideDatasource - This should be an object that can have the following two properties.
     * `getRows`, a function that gets a payload about fetching more data for the grid. This will get
     * sent a params object with infomation about row numbers requested, group information, sort information,
     * etc.
     * `destroy`, a optional function that is called when the grid is destroyed.
     * Documentation for this can be found here:
     * https://www.ag-grid.com/vue-data-grid/server-side-model-datasource/#registering-the-datasource
     */
    setServerSideData(isServerSideGroup, getServerSideGroupKey, serverSideDatasource) {
        this.rowModelType = 'serverSide';
        this.isServerSideGroup = isServerSideGroup;
        this.getServerSideGroupKey = getServerSideGroupKey;
        this.serverSideDatasource = serverSideDatasource;
        return this;
    }
    /**
     * Set sidebar support for this engine
     * @param sideBar
     * @returns
     */
    setSidebar(sideBar) {
        this.sideBar = sideBar;
        return this;
    }
}
/**
 * Helper for main AG Grid features
 */
class AgGridHelper {
    /**
     * Setup and initialize global modules for AG Grid
     * @returns
     */
    static registerModules(licenseKey = '') {
        if (this.registered)
            return;
        if (isNeString(licenseKey))
            LicenseManager.setLicenseKey(licenseKey);
        this.registered = true;
        ModuleRegistry.registerModules([
            ClientSideRowModelModule,
            RowGroupingModule,
            ServerSideRowModelModule,
            CsvExportModule,
            ClipboardModule,
            ExcelExportModule,
            MenuModule,
            RangeSelectionModule,
            SetFilterModule,
            SideBarModule,
            ColumnsToolPanelModule,
            FiltersToolPanelModule,
        ]);
    }
}
AgGridHelper.registered = false;
export { TableEngine, ColHelper, ColGroupHelper, SizeStrategyHelper, AUTO_SIZE_TYPE, AG_COLUMN_FILTER_TYPES, DEFAULT_COLUMN_TYPES, MEAD_TABLE_COLUMN_TYPES, GROUP_AUTO_COLUMN_ID, AgGridHelper };
export default TableEngine;
//# sourceMappingURL=AgGridTableEngine.js.map